// services/sectionService.ts
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy,
  Timestamp,
  writeBatch,
  arrayUnion,
  arrayRemove
} from "firebase/firestore";
import { db } from "../firebase/config";
import { Section } from "../types";

const SECTIONS_COLLECTION = "sections";
const EXAMS_COLLECTION = "exams";

// Helper to convert Firestore data to Section
const convertToSection = (doc: any, data: any): Section => {
  return {
    id: doc.id,
    name: data.name || "",
    description: data.description || "",
    instructions: data.instructions || "",
    order: data.order || 1,
    traits: data.traits || [],
    scaleType: data.scaleType || "",
    // New fields
    totalQuestions: data.totalQuestions || data.questionCount || 15,
    minQuestions: data.minQuestions || 10,
    selectionType: data.selectionType || "random",
    timeLimit: data.timeLimit || data.duration || 15,
    autoSubmit: data.autoSubmit !== undefined ? data.autoSubmit : true,
    weightage: data.weightage || 100,
    marksPerQuestion: data.marksPerQuestion || 0,
    negativeMarking: data.negativeMarking || false,
    isOptional: data.isOptional || false,
    passingScore: data.passingScore || 0,
    sectionType: data.sectionType === 'mixed' ? 'hybrid' : (data.sectionType || "psychometric"),
    allowedQuestionTypes: data.allowedQuestionTypes || [],
    // Legacy fields for backward compatibility
    questionCount: data.totalQuestions || data.questionCount || 15,
    duration: data.timeLimit || data.duration || 15,
    createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
    updatedAt: data.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
  };
};

export const getSections = async (): Promise<Section[]> => {
  try {
    const q = query(collection(db, SECTIONS_COLLECTION), orderBy("createdAt", "desc"));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => convertToSection(doc, doc.data()));
  } catch (error) {
    console.error("Error fetching sections:", error);
    throw error;
  }
};

export const getSectionById = async (id: string): Promise<Section | null> => {
  try {
    const docRef = doc(db, SECTIONS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return convertToSection(docSnap, docSnap.data());
    }
    return null;
  } catch (error) {
    console.error("Error fetching section:", error);
    throw error;
  }
};

export const createSection = async (sectionData: any): Promise<string> => {
  try {
    if (!sectionData.name) {
      throw new Error("Section name is required");
    }
    
    if ((sectionData.totalQuestions || sectionData.questionCount) <= 0) {
      throw new Error("Number of questions must be greater than 0");
    }
    
    if (sectionData.weightage <= 0 || sectionData.weightage > 100) {
      throw new Error("Weightage must be between 1 and 100");
    }
    
    if ((sectionData.sectionType === 'psychometric' || sectionData.sectionType === 'hybrid') && (!sectionData.traits || sectionData.traits.length === 0)) {
      throw new Error(`${sectionData.sectionType === 'psychometric' ? 'Psychometric' : 'Hybrid'} sections require at least one trait`);
    }
    
    if (sectionData.allowedQuestionTypes?.length === 0) {
      throw new Error("At least one question type must be selected");
    }
    
    const now = Timestamp.now();
    const dbSectionType = sectionData.sectionType === 'hybrid' ? 'mixed' : sectionData.sectionType;
    
    const sectionToCreate = {
      name: sectionData.name.trim(),
      description: sectionData.description || "",
      instructions: sectionData.instructions || "",
      order: Number(sectionData.order) || 1,
      traits: (sectionData.traits || []).map((trait: any, index: number) => ({
        traitId: trait.traitId || "",
        traitName: trait.traitName || "",
        traitIcon: trait.traitIcon || "📌",
        scaleId: trait.scaleId || "",
        scaleType: trait.scaleType || "",
        weightage: Number(trait.weightage) || 0,
        order: Number(trait.order) || index + 1
      })),
      scaleType: sectionData.scaleType || "",
      // New fields
      totalQuestions: Number(sectionData.totalQuestions || sectionData.questionCount || 15),
      minQuestions: Number(sectionData.minQuestions || 10),
      selectionType: sectionData.selectionType || "random",
      timeLimit: Number(sectionData.timeLimit || sectionData.duration || 15),
      autoSubmit: sectionData.autoSubmit !== undefined ? sectionData.autoSubmit : true,
      weightage: Number(sectionData.weightage) || 100,
      marksPerQuestion: Number(sectionData.marksPerQuestion) || 0,
      negativeMarking: sectionData.negativeMarking || false,
      isOptional: Boolean(sectionData.isOptional),
      passingScore: Number(sectionData.passingScore) || 0,
      sectionType: dbSectionType,
      allowedQuestionTypes: sectionData.allowedQuestionTypes || [],
      createdAt: now,
      updatedAt: now,
    };
    
    const docRef = await addDoc(collection(db, SECTIONS_COLLECTION), sectionToCreate);
    return docRef.id;
  } catch (error) {
    console.error("Error creating section:", error);
    throw error;
  }
};

export const updateSection = async (id: string, sectionData: any): Promise<void> => {
  try {
    const docRef = doc(db, SECTIONS_COLLECTION, id);
    const updateData: any = {
      updatedAt: Timestamp.now(),
    };
    
    if (sectionData.name !== undefined) updateData.name = sectionData.name.trim();
    if (sectionData.description !== undefined) updateData.description = sectionData.description;
    if (sectionData.instructions !== undefined) updateData.instructions = sectionData.instructions;
    if (sectionData.order !== undefined) updateData.order = Number(sectionData.order);
    if (sectionData.traits !== undefined) updateData.traits = sectionData.traits;
    if (sectionData.scaleType !== undefined) updateData.scaleType = sectionData.scaleType;
    // New fields
    if (sectionData.totalQuestions !== undefined) updateData.totalQuestions = Number(sectionData.totalQuestions);
    if (sectionData.minQuestions !== undefined) updateData.minQuestions = Number(sectionData.minQuestions);
    if (sectionData.selectionType !== undefined) updateData.selectionType = sectionData.selectionType;
    if (sectionData.timeLimit !== undefined) updateData.timeLimit = Number(sectionData.timeLimit);
    if (sectionData.autoSubmit !== undefined) updateData.autoSubmit = Boolean(sectionData.autoSubmit);
    if (sectionData.weightage !== undefined) updateData.weightage = Number(sectionData.weightage);
    if (sectionData.marksPerQuestion !== undefined) updateData.marksPerQuestion = Number(sectionData.marksPerQuestion);
    if (sectionData.negativeMarking !== undefined) updateData.negativeMarking = Boolean(sectionData.negativeMarking);
    if (sectionData.isOptional !== undefined) updateData.isOptional = Boolean(sectionData.isOptional);
    if (sectionData.passingScore !== undefined) updateData.passingScore = Number(sectionData.passingScore);
    if (sectionData.sectionType !== undefined) {
      updateData.sectionType = sectionData.sectionType === 'hybrid' ? 'mixed' : sectionData.sectionType;
    }
    if (sectionData.allowedQuestionTypes !== undefined) updateData.allowedQuestionTypes = sectionData.allowedQuestionTypes;
    
    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error("Error updating section:", error);
    throw error;
  }
};

export const deleteSection = async (id: string): Promise<void> => {
  try {
    const examsRef = collection(db, EXAMS_COLLECTION);
    const examsSnapshot = await getDocs(examsRef);
    const batch = writeBatch(db);
    
    examsSnapshot.forEach(examDoc => {
      const examData = examDoc.data();
      if (examData.sections && Array.isArray(examData.sections) && examData.sections.includes(id)) {
        const examRef = doc(db, EXAMS_COLLECTION, examDoc.id);
        batch.update(examRef, {
          sections: arrayRemove(id),
          updatedAt: Timestamp.now()
        });
      }
    });
    
    const sectionRef = doc(db, SECTIONS_COLLECTION, id);
    batch.delete(sectionRef);
    await batch.commit();
  } catch (error) {
    console.error("Error deleting section:", error);
    throw error;
  }
};

export const assignSectionToExam = async (examId: string, sectionId: string): Promise<void> => {
  try {
    const sectionRef = doc(db, SECTIONS_COLLECTION, sectionId);
    const sectionSnap = await getDoc(sectionRef);
    
    if (!sectionSnap.exists()) {
      throw new Error("Section not found");
    }
    
    const examRef = doc(db, EXAMS_COLLECTION, examId);
    const examSnap = await getDoc(examRef);
    
    if (!examSnap.exists()) {
      throw new Error("Exam not found");
    }
    
    const examData = examSnap.data();
    const currentSections = examData.sections || [];
    
    if (currentSections.includes(sectionId)) {
      throw new Error("Section is already assigned to this exam");
    }
    
    await updateDoc(examRef, {
      sections: arrayUnion(sectionId),
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error("Error assigning section to exam:", error);
    throw error;
  }
};

export const removeSectionFromExam = async (examId: string, sectionId: string): Promise<void> => {
  try {
    const examRef = doc(db, EXAMS_COLLECTION, examId);
    await updateDoc(examRef, {
      sections: arrayRemove(sectionId),
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error("Error removing section from exam:", error);
    throw error;
  }
};

export const getSectionsByExam = async (examId: string): Promise<Section[]> => {
  try {
    const examRef = doc(db, EXAMS_COLLECTION, examId);
    const examSnap = await getDoc(examRef);
    
    if (!examSnap.exists()) {
      return [];
    }
    
    const examData = examSnap.data();
    const sectionIds = examData.sections || [];
    
    if (sectionIds.length === 0) {
      return [];
    }
    
    const sections: Section[] = [];
    for (const sectionId of sectionIds) {
      const section = await getSectionById(sectionId);
      if (section) {
        sections.push(section);
      }
    }
    
    sections.sort((a, b) => (a.order || 0) - (b.order || 0));
    
    return sections;
  } catch (error) {
    console.error("Error fetching sections by exam:", error);
    throw error;
  }
};

export const getSectionsByType = async (sectionType: string): Promise<Section[]> => {
  try {
    const allSections = await getSections();
    return allSections.filter(section => {
      const dbType = section.sectionType === 'hybrid' ? 'mixed' : section.sectionType;
      const targetType = sectionType === 'hybrid' ? 'mixed' : sectionType;
      return dbType === targetType;
    });
  } catch (error) {
    console.error("Error fetching sections by type:", error);
    throw error;
  }
};

export const updateSectionOrder = async (sectionId: string, newOrder: number): Promise<void> => {
  try {
    const sectionRef = doc(db, SECTIONS_COLLECTION, sectionId);
    await updateDoc(sectionRef, {
      order: newOrder,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error("Error updating section order:", error);
    throw error;
  }
};

export const bulkUpdateSectionOrders = async (sections: { id: string; order: number }[]): Promise<void> => {
  try {
    const batch = writeBatch(db);
    
    for (const section of sections) {
      const sectionRef = doc(db, SECTIONS_COLLECTION, section.id);
      batch.update(sectionRef, {
        order: section.order,
        updatedAt: Timestamp.now()
      });
    }
    
    await batch.commit();
  } catch (error) {
    console.error("Error bulk updating section orders:", error);
    throw error;
  }
};

export const getFilteredSections = async (filters: {
  sectionType?: string;
  isOptional?: boolean;
  minWeightage?: number;
  maxWeightage?: number;
}): Promise<Section[]> => {
  try {
    let sections = await getSections();
    
    if (filters.sectionType) {
      const targetType = filters.sectionType === 'hybrid' ? 'mixed' : filters.sectionType;
      sections = sections.filter(s => {
        const dbType = s.sectionType === 'hybrid' ? 'mixed' : s.sectionType;
        return dbType === targetType;
      });
    }
    
    if (filters.isOptional !== undefined) {
      sections = sections.filter(s => s.isOptional === filters.isOptional);
    }
    
    if (filters.minWeightage !== undefined) {
      sections = sections.filter(s => s.weightage >= filters.minWeightage!);
    }
    
    if (filters.maxWeightage !== undefined) {
      sections = sections.filter(s => s.weightage <= filters.maxWeightage!);
    }
    
    return sections;
  } catch (error) {
    console.error("Error fetching filtered sections:", error);
    throw error;
  }
};